import { IndexPath, Select, SelectItem, Text } from "@ui-kitten/components";
import BaseCard from "components/BaseCard";
import MainContainer from "components/MainContainer";
import ProfileAvatar from "components/ProfileAvatar";
import Spacer from "components/Spacer";
import env from "config/env";
import { fetchPlanPackagesForUpgrade } from "features/slices/buyFlowSlice";
import { AccountMemberDetails, SubscriptionDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { SubscriptionDetailScreenProps } from "navigation/types";
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getUserName, memberId } from "utils/userUtils";
import MemberSubscriptionList from "./components/MemberSubscriptionList";

const findMember = (members: AccountMemberDetails[], id: number) => {
  return members.find((m) => m.id === id);
};

const findSubsByMemberId = (
  subscriptions: SubscriptionDetails[],
  memberId: number
) => {
  return subscriptions.filter(
    (sub) =>
      sub.member &&
      sub.member.id === memberId &&
      filterIneligibleSubscriptions(sub) &&
      !sub.child
  );
};

const SubscriptionDetail = ({
  navigation,
  route,
}: SubscriptionDetailScreenProps) => {
  const [currentMember, setCurrentMember] =
    useState<AccountMemberDetails | null>(null);

  const [currentMemberSubscriptions, setCurrentMemberSubscriptions] = useState<
    SubscriptionDetails[]
  >([]);

  const { upgradablePlans, step } = useAppSelector((state) => state.buyFlow);
  const dispatch = useAppDispatch();
  const memberIdParam = route.params.memberId;
  const { account } = useAccount();

  const goBack = () =>
    setTimeout(() => {
      navigation.navigate("ManageSubscriptionsScreen");
    }, 250);

  const shouldShowMemberInfo = () => {
    const haveOtherMembers = otherMembers.length > 0;
    return haveOtherMembers && step === 1;
  };

  const otherMembers =
    account?.members.filter((m) => m.id !== currentMember?.id) || [];

  const handleSelect = (memberId: number) => {
    if (!account) return;
    const member = findMember(account.members, memberId);
    setCurrentMember(member!);
  };

  // fetch available plan packages offered by tenant, if already initialized, skip
  useEffect(() => {
    if ((upgradablePlans && upgradablePlans.length > 0) || !account) return;
    dispatch(fetchPlanPackagesForUpgrade(account));
  }, []);

  // if the member id in url is invalid, go to previous screen
  useEffect(() => {
    if (!account) return;

    const member = findMember(account.members, parseInt(memberIdParam));

    if (member === undefined) {
      goBack();
      return;
    }
    setCurrentMember(member);
  }, [account]);

  // find corresponding member after member changes in drop down selector
  useEffect(() => {
    if (!currentMember) return;
    const memberSubscriptions = findSubsByMemberId(
      account!.subscriptions,
      currentMember.id
    );
    setCurrentMemberSubscriptions(memberSubscriptions);
  }, [currentMember, account]);

  if (currentMember === null) return null;

  return (
    <MainContainer goBackAction={goBack} grayBackground>
      <View style={{ padding: 15 }}>
        {shouldShowMemberInfo() && (
          <>
            <Select
              placeholder="Select different member"
              onSelect={(index) =>
                handleSelect(otherMembers![(index as IndexPath).row].id)
              }
            >
              {otherMembers?.map((member) => (
                <SelectItem
                  title={getUserName(member)}
                  key={member.id}
                ></SelectItem>
              ))}
            </Select>
            <Spacer />

            <BaseCard>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <View>
                  {currentMember.type === "PRIMARY" && (
                    <Text category="c1">Account Owner</Text>
                  )}
                  <Text category="h6">{getUserName(currentMember)}</Text>
                  <Text>Member ID: {memberId(currentMember.id)}</Text>
                </View>
                <View>
                  <ProfileAvatar
                    source={{
                      uri: `${env.fileUrl}/${currentMember.user.imageIdentifier}`,
                    }}
                    hasImageIdentifier={
                      currentMember.user.imageIdentifier !== null
                    }
                    size={64}
                  ></ProfileAvatar>
                </View>
              </View>
            </BaseCard>
          </>
        )}
        <Spacer />

        <MemberSubscriptionList
          currentMemberSubscriptions={currentMemberSubscriptions}
          memberId={currentMember.id}
        />
      </View>
    </MainContainer>
  );
};

export default SubscriptionDetail;
